// Main Layout Component
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';

import Navbar from './Navbar';
import Sidebar from './Sidebar';
import LoadingPage from './LoadingPage';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import CartDrawer from '../store/CartDrawer';
import AssistantLauncher from '../ai/AssistantLauncher';

const Layout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isTablet && !isMobile);
  const { loading, currentCompany, companies = [], switchCompany } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Los enlaces de los correos traen `?company=<id>` para abrir en la empresa
  // del propio correo. Sin esto, un usuario multiempresa pulsaba "Ver tickets"
  // en el reporte de una empresa y aterrizaba en la que tuviera activa,
  // viendo datos que no eran los del correo.
  useEffect(() => {
    const solicitada = searchParams.get('company');
    if (!solicitada || loading || !currentCompany) return;
    if (currentCompany.company_id === solicitada) {
      // Ya estamos donde toca: se limpia el parámetro para no repetir el cambio
      // al navegar dentro de la app.
      searchParams.delete('company');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    // Solo se cambia si el usuario pertenece a esa empresa; si no, se ignora
    // en silencio y se queda donde está (el backend tampoco lo permitiría).
    const permitida = companies.some((c) => c.company_id === solicitada);
    if (!permitida) {
      searchParams.delete('company');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    switchCompany?.(solicitada).finally(() => {
      searchParams.delete('company');
      setSearchParams(searchParams, { replace: true });
    });
  }, [searchParams, loading, currentCompany, companies, switchCompany, setSearchParams]);

  const { drawerOpen, closeCartDrawer } = useCart();

  // Auto-collapse on tablet screens
  useEffect(() => {
    if (isTablet && !isMobile) {
      setSidebarCollapsed(true);
    } else if (!isTablet && !isMobile) {
      setSidebarCollapsed(false);
    }
  }, [isTablet, isMobile]);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleSidebarClose = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  const SIDEBAR_WIDTH_EXPANDED = 280;
  const SIDEBAR_WIDTH_COLLAPSED = 64;
  const NAVBAR_HEIGHT = 64;

  const sidebarWidth = isMobile
    ? 0
    : sidebarCollapsed
      ? SIDEBAR_WIDTH_COLLAPSED
      : SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      {/* Top Navigation - Always full width */}
      <Navbar
        onSidebarToggle={handleSidebarToggle}
        sidebarOpen={sidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        height={NAVBAR_HEIGHT}
      />

      {/* Content area */}
      <Box sx={{ display: 'flex', flex: 1, position: 'relative' }}>
        {/* Sidebar */}
        <Sidebar
          open={sidebarOpen}
          collapsed={sidebarCollapsed}
          onClose={handleSidebarClose}
          widthExpanded={SIDEBAR_WIDTH_EXPANDED}
          widthCollapsed={SIDEBAR_WIDTH_COLLAPSED}
          isMobile={isMobile}
          navbarHeight={NAVBAR_HEIGHT}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            marginLeft: `${sidebarWidth - sidebarWidth}px`,
            marginTop: `${NAVBAR_HEIGHT}px`,
            transition: theme.transitions.create('margin-left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            // Alto DEFINIDO, no mínimo: sin esto una página que pide `height: 100%`
            // (el chat, que es una app de alto completo) no tiene contra qué
            // resolver el porcentaje y se desborda, dejando dos barras de scroll.
            // Para el resto no cambia nada: el contenido largo sigue haciendo
            // scroll dentro del contenedor de página, que es quien lo tenía.
            height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
            overflow: 'hidden',
          }}
        >
          {/* Page Content */}
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: 'background.default',
              p: { xs: 2, sm: 3 },
              overflowY: 'auto',
              height: '100%',
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>

      <CartDrawer
        open={drawerOpen}
        onClose={closeCartDrawer}
        onCheckoutClick={() => {
          closeCartDrawer();
          navigate('/checkout');
        }}
      />

      {/* Asistente APS (IA) — botón flotante + drawer, disponible en toda la web */}
      <AssistantLauncher />
    </Box>
  );
};

export default Layout;